/**
 * ResponsiveTable — the shared admin table primitive (DESIGN.md "Wide admin
 * tables").
 *
 * One DOM tree, two presentations: a real <table> at and above the breakpoint,
 * a stack of cards below it. Nothing is duplicated or conditionally rendered —
 * only `display` changes — so row state, focus, and tests are unaffected by
 * viewport.
 *
 * Carried across from the RMS repo whole, the way `shared/` and DESIGN.md
 * were at the fork, and free to drift from there: it is one product's
 * table, and intake's first destination should not invent a second
 * answer to what a row looks like on a phone (#85).
 *
 * Two call-site decisions the primitive deliberately does NOT make for you:
 *
 *   scroll="cards"  — below the breakpoint each row becomes a bordered card.
 *                     The only safe choice for a table containing an
 *                     absolutely-positioned dropdown: `overflow-x: auto`
 *                     implies `overflow-y: auto`, which clips a dropdown hung
 *                     off the bottom rows.
 *   scroll="x-auto" — the wide grid scrolls horizontally at and above the
 *                     breakpoint instead: the table keeps its min width and the
 *                     wrapper div scrolls. Permitted ONLY where no cell contains
 *                     an absolutely-positioned dropdown (e.g. the Inquiries
 *                     list, whose rows are plain links).
 *
 * Each role below is a pair of module classes rather than one — a CSS Module
 * cannot take a variable media query, so `sm` and `md` are written out and the
 * `breakpoint` prop picks the pair.
 */
import {
  createContext,
  useContext,
  type ReactNode,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from "react";
import styles from "./ResponsiveTable.module.css";

export type TableBreakpoint = "sm" | "md";

interface BreakpointClasses {
  head: string;
  body: string;
  row: string;
  cell: string;
  cardRow: string;
  labelOff: string;
  alignRight: string;
  staticCell: string;
  /** Wrapper div when scroll="x-auto" — the scroll container. */
  scrollWrap: string;
  /** The table's min width when scroll="x-auto" — what makes it scroll. */
  scrollTable: string;
}

const CLASSES: Record<TableBreakpoint, BreakpointClasses> = {
  sm: {
    head: styles.headSm,
    body: styles.bodySm,
    row: styles.rowSm,
    cell: styles.cellSm,
    cardRow: styles.cardRowSm,
    labelOff: styles.labelOffSm,
    alignRight: styles.alignRightSm,
    staticCell: styles.staticCellSm,
    scrollWrap: styles.scrollWrapSm,
    scrollTable: styles.scrollTableSm,
  },
  md: {
    head: styles.headMd,
    body: styles.bodyMd,
    row: styles.rowMd,
    cell: styles.cellMd,
    cardRow: styles.cardRowMd,
    labelOff: styles.labelOffMd,
    alignRight: styles.alignRightMd,
    staticCell: styles.staticCellMd,
    scrollWrap: styles.scrollWrapMd,
    scrollTable: styles.scrollTableMd,
  },
};

/** Mobile card grid width, and the spans a cell may claim within it. */
const CARD_COLUMNS = {
  1: styles.columns1,
  2: styles.columns2,
  4: styles.columns4,
} as const;

const CARD_SPANS = {
  1: styles.span1,
  2: styles.span2,
  4: styles.span4,
} as const;

export type CardColumns = keyof typeof CARD_COLUMNS;
export type CardSpan = keyof typeof CARD_SPANS;

const BreakpointContext = createContext<TableBreakpoint>("sm");
const ColumnsContext = createContext<CardColumns>(4);

const useTableClasses = () => CLASSES[useContext(BreakpointContext)];

function cx(...parts: (string | false | undefined)[]) {
  return parts.filter(Boolean).join(" ");
}

interface ResponsiveTableProps {
  children: ReactNode;
  /** Width at which the card stack reverts to a real table. Default `sm`. */
  breakpoint?: TableBreakpoint;
  /** See the file header — `x-auto` is unsafe with in-cell dropdowns. */
  scroll?: "cards" | "x-auto";
  className?: string;
}

export function ResponsiveTable({
  children,
  breakpoint = "sm",
  scroll = "cards",
  className,
}: ResponsiveTableProps) {
  const c = CLASSES[breakpoint];
  const table = (
    <table
      className={cx(
        styles.table,
        scroll === "x-auto" && c.scrollTable,
        className,
      )}
    >
      {children}
    </table>
  );
  return (
    <BreakpointContext.Provider value={breakpoint}>
      {scroll === "x-auto" ? (
        <div className={c.scrollWrap}>{table}</div>
      ) : (
        table
      )}
    </BreakpointContext.Provider>
  );
}

/** Column headings. Hidden below the breakpoint — cells carry `label` instead. */
export function TableHead({ children }: { children: ReactNode }) {
  return (
    <thead className={useTableClasses().head}>
      <tr className={styles.headRow}>{children}</tr>
    </thead>
  );
}

interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: "left" | "right";
  /** Replaces the default outright — see `TableCell`'s `padding`. */
  padding?: string;
}

export function TableHeaderCell({
  align = "left",
  padding = styles.cellPadding,
  className,
  children,
  ...rest
}: TableHeaderCellProps) {
  return (
    <th
      className={cx(
        padding,
        align === "right" ? styles.headerCellRight : styles.headerCell,
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export function TableBody({ children }: { children: ReactNode }) {
  return <tbody className={useTableClasses().body}>{children}</tbody>;
}

interface TableRowProps {
  children: ReactNode;
  /**
   * `card` (default) — a bordered card below the breakpoint, for data rows.
   * `plain` — no card treatment, for group headings, subtotals, and full-width
   * notices that read as dividers rather than records.
   */
  variant?: "card" | "plain";
  /**
   * Mobile card grid width. `4` (default) suits the quote/gear shape: a
   * full-width name row over paired numeric fields. `1` stacks every field
   * label-above-value — the right choice for many narrow inputs (the RMS's
   * ten-column day schedule), where a 4-col grid just cramps vertically.
   */
  columns?: CardColumns;
  className?: string;
}

export function TableRow({
  children,
  variant = "card",
  columns = 4,
  className,
}: TableRowProps) {
  const c = useTableClasses();
  return (
    <ColumnsContext.Provider value={columns}>
      <tr
        className={cx(
          variant === "card" ? cx(c.cardRow, CARD_COLUMNS[columns]) : c.row,
          className,
        )}
      >
        {children}
      </tr>
    </ColumnsContext.Provider>
  );
}

interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /**
   * Column name surfaced below the breakpoint via `data-label` + `::before`,
   * where the <thead> is hidden.
   *
   * The pseudo-element is presentational and is NOT reliably announced, so any
   * input inside the cell must carry its own `aria-label` (DESIGN.md
   * Accessibility). Omit `label` on cells whose content is self-describing
   * (an icon button, a spanning heading).
   */
  label?: string;
  /** Columns claimed in the mobile card grid. Clamped to the row's width. */
  span?: CardSpan;
  align?: "left" | "right";
  /**
   * Pulls the cell out of the card flow and into its top-right corner below
   * the breakpoint — for a row's delete/overflow control.
   */
  corner?: boolean;
  /**
   * Cell padding, as a class name — the call site's own module class.
   * Replaces the default outright rather than merging: two classes on one
   * element resolve by the stylesheet's source order, not by the call site's
   * intent, so exactly one wins and the call site is the one that picks it.
   * Tables of inline-edit inputs want something tighter than the default.
   */
  padding?: string;
}

export function TableCell({
  label,
  span = 2,
  align = "left",
  corner = false,
  padding = styles.cellPadding,
  className,
  children,
  ...rest
}: TableCellProps) {
  const c = useTableClasses();
  const columns = useContext(ColumnsContext);
  // A cell can't claim more of the card grid than the row actually has.
  const effectiveSpan = Math.min(span, columns) as CardSpan;
  return (
    <td
      data-label={label}
      className={cx(
        corner
          ? cx(styles.cornerCell, c.staticCell)
          : cx(CARD_SPANS[effectiveSpan], c.cell),
        padding,
        label && cx(styles.labelledCell, c.labelOff),
        align === "right" && c.alignRight,
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}
